import { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { UnlockedEntry } from '../qr/types';
import { buildCharacter, type BuiltCharacter } from '../character/builder';
import { useNav } from '../stores/navigation';

type Props = { entry: UnlockedEntry };

export function CharacterDetailScreen({ entry }: Props) {
  const go = useNav((s) => s.go);
  const mountRef = useRef<HTMLDivElement>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const builtRef = useRef<BuiltCharacter | null>(null);
  const [animName, setAnimName] = useState<string | null>(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    let raf = 0;
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x0a0a14, 1);
    // canvas を親要素にフィットさせる（無限リサイズループ防止）
    renderer.domElement.style.display = 'block';
    renderer.domElement.style.width = '100%';
    renderer.domElement.style.height = '100%';
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();

    const camera = new THREE.PerspectiveCamera(35, 1, 0.1, 100);
    // カメラ・注視点を上げて、原点(=キャラ足元)を画面下寄りに置く
    camera.position.set(0, 1.5, 5);
    camera.lookAt(0, 1.0, 0);

    // 3D ビューワーでは 1/3 サイズの 80% (≒ 0.267) で表示
    const VIEWER_SCALE = (1 / 3) * 0.8;

    const ambient = new THREE.AmbientLight(0xffffff, 0.6);
    const dir = new THREE.DirectionalLight(0xffffff, 1.0);
    dir.position.set(3, 5, 4);
    scene.add(ambient, dir);

    const clock = new THREE.Clock();
    let mixer: THREE.AnimationMixer | null = null;
    let dispose: (() => void) | null = null;

    let pendingResize = false;
    function resize() {
      if (pendingResize) return;
      pendingResize = true;
      requestAnimationFrame(() => {
        pendingResize = false;
        const { width, height } = mount!.getBoundingClientRect();
        if (width === 0 || height === 0) {
          console.warn('[Detail] mount has zero size', { width, height });
          return;
        }
        renderer.setSize(width, height, false);
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        console.log('[Detail] resize', { width, height });
      });
    }
    const ro = new ResizeObserver(resize);
    ro.observe(mount);
    resize();

    console.log('[Detail] mounting for', entry);

    buildCharacter({ m: entry.m, b: entry.b, f: entry.f, h: entry.h, w: entry.w, e: entry.e })
      .then((built) => {
        if (cancelled) {
          built.dispose();
          return;
        }
        built.root.scale.multiplyScalar(VIEWER_SCALE);
        scene.add(built.root);
        mixer = built.mixer;
        dispose = built.dispose;
        builtRef.current = built;
        setAnimName(built.currentAnimation);
        console.log('[Detail] character added', {
          animations: built.animationNames,
          current: built.currentAnimation,
        });
      })
      .catch((err) => {
        console.error('[Detail] character load failed', err);
      });

    function loop() {
      raf = requestAnimationFrame(loop);
      const dt = clock.getDelta();
      mixer?.update(dt);
      scene.rotation.y += dt * 0.4; // 自動回転（仮）
      renderer.render(scene, camera);
    }
    loop();

    cleanupRef.current = () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      dispose?.();
      renderer.dispose();
      if (renderer.domElement.parentElement === mount) {
        mount.removeChild(renderer.domElement);
      }
    };

    return () => {
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [entry.m, entry.b, entry.f, entry.h, entry.w, entry.e]);

  // ダブルタップでアニメーション切替
  const lastTapRef = useRef(0);
  function onMountClick() {
    const now = performance.now();
    if (now - lastTapRef.current < 320) {
      const next = builtRef.current?.cycleAnimation() ?? null;
      setAnimName(next);
      lastTapRef.current = 0;
    } else {
      lastTapRef.current = now;
    }
  }

  return (
    <div className="screen">
      <button
        className="btn btn-secondary"
        onClick={() => go({ kind: 'collection' })}
        style={{ alignSelf: 'flex-start' }}
      >
        ← 戻る
      </button>
      <h2 className="screen-title">キャラクター</h2>

      <div
        ref={mountRef}
        onClick={onMountClick}
        style={{
          flex: 1,
          width: '100%',
          minHeight: 240,
          background: '#0a0a14',
          borderRadius: 16,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {animName && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              left: 8,
              background: 'rgba(0,0,0,0.55)',
              color: '#fff',
              padding: '4px 10px',
              borderRadius: 12,
              fontSize: 12,
              pointerEvents: 'none',
            }}
          >
            ▶ {animName}
          </div>
        )}
        <div
          style={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            color: 'rgba(255,255,255,0.55)',
            fontSize: 11,
            pointerEvents: 'none',
          }}
        >
          ダブルタップでアニメ切替
        </div>
      </div>

      <div className="card" style={{ marginTop: 12, fontFamily: 'monospace', fontSize: 13 }}>
        <div>model: character_{String(entry.m).padStart(2, '0')}.glb</div>
        <div>body: {entry.b}</div>
        <div>face: face_texture_{entry.f}.png</div>
        <div>size: h{entry.h} / w{entry.w}</div>
      </div>

      <button
        className="btn btn-block"
        onClick={() => go({ kind: 'ar', entry })}
        style={{ marginTop: 12 }}
      >
        AR で見る
      </button>
    </div>
  );
}
